package dev.wyns.ventrova.data.model

import kotlin.math.PI
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.sqrt

/** A venue with vegan options. */
data class Place(
    val id: String,
    val name: String,
    val category: PlaceCategory,
    val veganLevel: VeganLevel,
    val location: GeoPoint,
    /** Cuisine tags as stored in the database, e.g. `pizza`, `italian`. */
    val cuisines: List<String>,
    val address: Address,
    val website: String?,
    val phone: String?,
    val email: String?,
    /** Null when the opening hours are unknown or could not be parsed. */
    val openingHours: OpeningHours?,
    /** What the place fries in according to its website; null when unknown. */
    val fryOil: FryOil?,
    /** Dish counts of the analysed menu; null when there is no menu. */
    val menu: MenuStats? = null,
)

data class GeoPoint(
    val latitude: Double,
    val longitude: Double,
) {
    /** Great-circle distance in meters. */
    fun distanceTo(other: GeoPoint): Double {
        val dLat = (other.latitude - latitude).toRadians()
        val dLng = (other.longitude - longitude).toRadians()
        val h = sin(dLat / 2).pow(2) + cos(latitude.toRadians()) * cos(other.latitude.toRadians()) * sin(dLng / 2).pow(2)
        return 2 * EARTH_RADIUS_METERS * asin(sqrt(h))
    }

    private companion object {
        const val EARTH_RADIUS_METERS = 6_371_000.0
    }
}

private fun Double.toRadians() = this * PI / 180

data class Address(
    val street: String?,
    val houseNumber: String?,
    val postcode: String?,
    val city: String?,
) {
    /** E.g. `Hoogpoort 14, 9000 Gent`; null when no part of the address is known. */
    fun formatted(): String? {
        val streetLine = listOfNotNull(street, houseNumber).joinToString(" ").ifBlank { null }
        val cityLine = listOfNotNull(postcode, city).joinToString(" ").ifBlank { null }
        return listOfNotNull(streetLine, cityLine).joinToString(", ").ifBlank { null }
    }
}

data class BoundingBox(
    val south: Double,
    val west: Double,
    val north: Double,
    val east: Double,
) {
    operator fun contains(point: GeoPoint): Boolean =
        point.latitude in south..north && point.longitude in west..east
}
